import cn from 'classnames'
import isNil from 'lodash/isNil'
import React, { useMemo, useCallback } from 'react'

import DropDownMenu from '@/common/components/DropDownMenu'
import DropDownRange from '@/common/components/DropDownRange'
import Gap from '@/common/components/Gap'
import Image from '@/common/components/Image'
import Table, { TableProps } from '@/common/components/Table'
import useI18n from '@/common/hooks/useI18n'
import { ACTION } from '@/common/translation'
import { getTraslationObject } from '@/common/utils/object'
import { numberToString } from '@/common/utils/string'
import { getFileUrl, resolvePath } from '@/common/utils/url'
import OwnerListingsAction from '@/modules/me/pages/OwnerListingsPage/components/OwnerListingsAction'
import OwnerListingsMoreDetail from '@/modules/me/pages/OwnerListingsPage/components/OwnerListingsMoreDetail'
import {
  PRICE_MIN,
  PRICE_MAX,
  PRICE_STEP,
  ROOM_STATUS_OPTIONS,
  OWNER_LISTING_STATUS_OPTIONS,
} from '@/modules/me/pages/OwnerListingsPage/constants'
import {
  ROOM_STATUS,
  PLACE_DETAIL,
  RENTAL_PRICE,
  OWNER_STATUS,
  RENTAL_HISTORY,
  BROKER,
  BROKER_LIST,
  LISTING_VIEWS,
} from '@/modules/me/pages/OwnerListingsPage/translation'
import {
  ME_OWNER_LISTING_RENTAL_HISTORY_PATH,
  ME_OWNER_LISTING_BORKER_LIST_PATH,
} from '@/modules/me/paths'
import getRoomDisplayName from '@/modules/property/utils/getRoomDisplayName'

import { DotStatus, StyledButton, MoreDetailWrapper } from './styled'

const DROP_DOWN_PROPS = { labelClassName: 'text-12 text-medium text-nowrap' }

interface OwnerListingsTableProps
  extends Omit<TableProps<RentAPerfect.OwnerRoom>, 'columns'> {
  filter: {
    roomStatus?: string
    roomApprovalStatus?: string
    price: [number, number]
  }
  onFilterChange: (key: string) => (value: any) => void
}

const OwnerListingsTable = ({
  data,
  filter,
  onFilterChange,
  ...props
}: OwnerListingsTableProps) => {
  const { t } = useI18n()

  const columns: TableProps['columns'] = useMemo(
    () => [
      {
        id: 'roomStatus',
        Header: () => (
          <Gap className='align-start' space={4} vertical>
            <div className='text-12 text-semibold text-nowrap'>
              {t(ROOM_STATUS)}
            </div>
            <DropDownMenu
              value={filter.roomStatus}
              options={ROOM_STATUS_OPTIONS}
              onChange={onFilterChange('roomStatus')}
              dropDownProps={DROP_DOWN_PROPS}
            />
          </Gap>
        ),
        accessor: ({ isAvailable, RoomPhotos }: RentAPerfect.OwnerRoom) => (
          <Gap className='align-center' space={12}>
            <DotStatus $isAvailable={isAvailable} />
            <Image
              src={getFileUrl(RoomPhotos?.[0]?.url)}
              className='radius-8'
              width={65}
              height={40}
              cover
            />
          </Gap>
        ),
      },
      {
        id: 'place',
        Header: () => (
          <div className='text-12 text-semibold text-left'>
            {t(PLACE_DETAIL)}
          </div>
        ),
        accessor: (room: RentAPerfect.OwnerRoom) => (
          <div>
            {room.Condo ? (
              <div className='text-12 text-medium'>
                {t(getTraslationObject(room.Condo, 'name'))}
              </div>
            ) : null}
            <div className='text-primary text-10 text-medium'>
              {getRoomDisplayName({ t, room })}
            </div>
            {!isNil(room.viewNumber) ? (
              <div className='text-gray text-10 text-medium'>
                {numberToString(room.viewNumber)}{' '}
                {t(LISTING_VIEWS, { n: room.viewNumber })}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'price',
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-12 text-semibold text-nowrap'>
              {t(RENTAL_PRICE)}
            </div>
            <DropDownRange
              value={filter.price}
              onChange={onFilterChange('price')}
              prefixLabel='฿'
              min={PRICE_MIN}
              max={PRICE_MAX}
              step={PRICE_STEP}
              dropDownProps={DROP_DOWN_PROPS}
            />
          </Gap>
        ),
        accessor: ({ RoomContracts }: RentAPerfect.OwnerRoom) => (
          <div className='text-center text-12 text-medium'>
            {RoomContracts ? `฿${numberToString(RoomContracts.price)}` : '-'}
          </div>
        ),
      },
      {
        id: 'approvalStatus',
        Header: () => (
          <Gap className='align-start' space={4} vertical>
            <div className='text-12 text-semibold text-nowrap'>
              {t(OWNER_STATUS)}
            </div>
            <DropDownMenu
              value={filter.roomApprovalStatus}
              options={OWNER_LISTING_STATUS_OPTIONS}
              onChange={onFilterChange('roomApprovalStatus')}
              dropDownProps={DROP_DOWN_PROPS}
            />
          </Gap>
        ),
        accessor: ({ approvalStatus }: RentAPerfect.OwnerRoom) => (
          <div
            className={cn(
              'text-center text-12 text-medium',
              approvalStatus === 'Verified' && 'text-success',
              approvalStatus === 'Unverified' && 'text-error'
            )}
          >
            {t(
              OWNER_LISTING_STATUS_OPTIONS.find(
                ({ value }) => value === approvalStatus
              )?.label ?? '-'
            )}
          </div>
        ),
      },
      {
        id: 'history',
        Header: () => (
          <div className='text-12 text-semibold'>{t(RENTAL_HISTORY)}</div>
        ),
        accessor: ({ id, isApproved }: RentAPerfect.OwnerRoom) =>
          isApproved ? (
            <StyledButton
              to={resolvePath(ME_OWNER_LISTING_RENTAL_HISTORY_PATH, {
                roomId: id,
              })}
            >
              {t(RENTAL_HISTORY)}
            </StyledButton>
          ) : (
            <div className='text-center text-12 text-medium'>-</div>
          ),
      },
      {
        id: 'broker',
        Header: () => <div className='text-12 text-semibold'>{t(BROKER)}</div>,
        accessor: ({ id, isApproved }: RentAPerfect.OwnerRoom) =>
          isApproved ? (
            <StyledButton
              to={resolvePath(ME_OWNER_LISTING_BORKER_LIST_PATH, {
                roomId: id,
              })}
            >
              {t(BROKER_LIST)}
            </StyledButton>
          ) : (
            <div className='text-center text-12 text-medium'>-</div>
          ),
      },
      {
        id: 'action',
        Header: () => (
          <div className='text-12 text-semibold text-nowrap'>{t(ACTION)}</div>
        ),
        accessor: (room: RentAPerfect.OwnerRoom) => (
          <div
            className='flex justify-center'
            onClick={event => event.stopPropagation()}
          >
            <OwnerListingsAction room={room} />
          </div>
        ),
      },
    ],
    [t, filter, onFilterChange]
  )

  const renderExpandedComponent = useCallback(({ row: { original: room } }) => {
    return (
      <MoreDetailWrapper>
        <OwnerListingsMoreDetail room={room} />
      </MoreDetailWrapper>
    )
  }, [])

  return (
    <Table
      {...props}
      data={data}
      columns={columns}
      renderExpandedComponent={renderExpandedComponent}
      expandedContentMaxHeight={750}
    />
  )
}

export default OwnerListingsTable
